import { supabase } from "../../supabase";
import {
  applyOrganizationScope,
  orgScopedInsert,
  orgScopedUpdateByPk,
  resolveOrganizationIdForQueries,
} from "../orgQueryScope";
import {
  DEFAULT_CLASSIFICATIONS,
} from "./catalogDefaults";
import {
  loadCatalogCache,
  persistCatalogCache,
  sortCatalogItems,
  toDropdownOptions,
  validateCatalogName,
} from "./catalogHelpers";

const TABLE = "classifications";
const ID_COLUMN = "classification_id";
const CACHE_KEY = "CP_CLASSIFICATIONS_CACHE_V1";

function normalizeClassificationRow(row) {
  return {
    classification_id: row.classification_id,
    organization_id: row.organization_id ?? null,
    name: row.name,
    display_order: Number(row.display_order ?? 0),
    is_active: row.is_active !== false,
    is_default: row.is_default === true,
    created_at: row.created_at,
    updated_at: row.updated_at,
  };
}

export function getFallbackClassifications() {
  return DEFAULT_CLASSIFICATIONS.map((entry, index) => ({
    classification_id: `fallback-${index}`,
    organization_id: null,
    name: entry.name,
    display_order: entry.display_order,
    is_active: true,
    is_default: index === 0,
  }));
}

export function classificationsToDropdownOptions(
  items,
  { includeEmpty = true } = {},
) {
  return toDropdownOptions(sortCatalogItems(items), {
    valueKey: "name",
    labelKey: "name",
    includeEmpty,
    emptyLabel: "Select Classification",
  });
}

export async function listClassifications(client = supabase, options = {}) {
  const { activeOnly = false, organizationId = null, actor = null } = options;

  const resolvedOrgId =
    organizationId ??
    (await resolveOrganizationIdForQueries(actor, client));

  let query = client
    .from(TABLE)
    .select(
      "classification_id, organization_id, name, display_order, is_active, is_default, created_at, updated_at",
    )
    .order("display_order", { ascending: true })
    .order("name", { ascending: true });

  query = applyOrganizationScope(query, resolvedOrgId, { tableName: TABLE });

  if (activeOnly) {
    query = query.eq("is_active", true);
  }

  const { data, error } = await query;
  if (error) {
    throw error;
  }

  return (data || []).map(normalizeClassificationRow);
}

/**
 * Active classifications for dropdowns.
 * Successful empty org catalog → []. Cache/fallback only on network/error.
 */
export async function loadClassificationsForConsumers(client = supabase, actor = null) {
  const organizationId = await resolveOrganizationIdForQueries(actor, client);

  try {
    const rows = await listClassifications(client, { activeOnly: true, actor });
    persistCatalogCache(CACHE_KEY, rows, organizationId);
    return rows;
  } catch (err) {
    console.warn("[classificationCatalog] load failed — using cache/fallback", err);
  }

  const cached = loadCatalogCache(CACHE_KEY, organizationId);
  if (cached?.length) {
    return cached.map(normalizeClassificationRow);
  }

  return getFallbackClassifications();
}

export async function getClassificationDropdownOptions(
  client = supabase,
  actor = null,
) {
  const items = await loadClassificationsForConsumers(client, actor);
  return classificationsToDropdownOptions(items);
}

export async function createClassification(
  { name, display_order, is_default = false },
  client = supabase,
  actor = null,
) {
  const validationError = validateCatalogName(name, "Classification name");
  if (validationError) {
    throw new Error(validationError);
  }

  const { data, error } = await orgScopedInsert(
    client,
    TABLE,
    [
      {
        name: name.trim(),
        display_order: Number(display_order ?? 0),
        is_default,
        is_active: true,
      },
    ],
    actor,
    {
      select:
        "classification_id, organization_id, name, display_order, is_active, is_default, created_at, updated_at",
      single: true,
    },
  );

  if (error) {
    throw error;
  }

  return normalizeClassificationRow(data);
}

export async function updateClassification(
  classificationId,
  patch,
  client = supabase,
  actor = null,
) {
  if (!actor) {
    throw new Error("Signed-in administrator required.");
  }

  if (patch.name != null) {
    const validationError = validateCatalogName(patch.name, "Classification name");
    if (validationError) {
      throw new Error(validationError);
    }
  }

  const payload = {
    ...(patch.name != null ? { name: patch.name.trim() } : {}),
    ...(patch.display_order != null
      ? { display_order: Number(patch.display_order) }
      : {}),
    ...(patch.is_active != null ? { is_active: patch.is_active } : {}),
    ...(patch.is_default != null ? { is_default: patch.is_default } : {}),
  };

  const builder = await orgScopedUpdateByPk(client, TABLE, payload, actor, {
    pkColumn: ID_COLUMN,
    pkValue: classificationId,
  });

  if (builder?.error) {
    throw builder.error;
  }

  const { data, error } = await builder
    .select(
      "classification_id, organization_id, name, display_order, is_active, is_default, created_at, updated_at",
    )
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    throw new Error("Classification not found in your organization.");
  }

  return normalizeClassificationRow(data);
}

export async function setDefaultClassification(
  classificationId,
  client = supabase,
  actor = null,
) {
  const organizationId = await resolveOrganizationIdForQueries(actor, client);

  if (!organizationId) {
    throw new Error("organization_id is required for classification update.");
  }

  let clearQuery = client
    .from(TABLE)
    .update({ is_default: false })
    .neq(ID_COLUMN, classificationId);

  clearQuery = applyOrganizationScope(clearQuery, organizationId, {
    tableName: TABLE,
  });

  const { error: clearError } = await clearQuery;
  if (clearError) {
    throw clearError;
  }

  return updateClassification(
    classificationId,
    { is_default: true },
    client,
    actor,
  );
}

export async function deactivateClassification(
  classificationId,
  client = supabase,
  actor = null,
) {
  return updateClassification(
    classificationId,
    { is_active: false },
    client,
    actor,
  );
}

export async function activateClassification(
  classificationId,
  client = supabase,
  actor = null,
) {
  return updateClassification(
    classificationId,
    { is_active: true },
    client,
    actor,
  );
}
